import { useState, useEffect, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import { useStore } from '../../../store/store';
import { API_ENDPOINTS } from '../../../api/endpoints';
import { cancelRequest } from '../../../api/api';
import PeriodForm from './PeriodForm';
import { formatPeriodDate, isoDay, spanInDays } from '../../../utils/contractPeriods';

/**
 * A company's contract, as the sequence of periods it actually is (TD-2812).
 *
 * The point of the panel is that a download allowance is per period, not per
 * lifetime: the same company counts again when a new period starts. So the list
 * shows where each period begins and ends, which one is running now, and what it
 * grants — and editing the running one is how a limit gets raised.
 */

/**
 * A period's limit, plus whatever it carried in from the period before.
 *
 * The company row enforces the TOTAL, because mirrorToCompany writes
 * limit + carried onto it. Showing only the agreed figure here is how an admin
 * ends up comparing 500 against the 880 in the fields above, "correcting" the
 * period to 880, and handing the customer 1,260 at the next boundary.
 */
const formatLimit = (value, carried) => {
  if (value === null || value === undefined) return '—';
  const base = Number(value).toLocaleString();
  const carriedIn = Number(carried) || 0;
  if (carriedIn <= 0) return base;
  return `${base} + ${carriedIn.toLocaleString()}`;
};

const todayIso = () => new Date().toISOString().split('T')[0];

/** What ending a period early did, in sentences the admin can check. */
const describeEnd = (period, endsOn, removedCount, autoRenewStopped) => {
  const parts = [`Period ${period.seq} now ends on ${formatPeriodDate(endsOn)}.`];
  if (removedCount) {
    const noun = removedCount === 1 ? 'renewal' : 'renewals';
    const verb = removedCount === 1 ? 'was' : 'were';
    parts.push(`${removedCount} automatic ${noun} that had not started yet ${verb} removed.`);
  }
  if (autoRenewStopped) {
    parts.push(
      'Automatic renewal is switched off; switch it on again above if the contract continues.'
    );
  }
  return parts.join(' ');
};

const ContractPeriods = ({ companyId, onCompanyChanged, onPeriodsLoaded }) => {
  const store = useStore();

  const [periods, setPeriods] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [editing, setEditing] = useState(null);
  // The period being ended early, and the date entered for it. An inline row
  // rather than prompt(): a dialog the browser owns cannot be styled, focused or
  // validated, and returns null inside a sandboxed frame, so the button did
  // nothing there.
  const [ending, setEnding] = useState(null);
  const [endDate, setEndDate] = useState('');

  // Read through a ref so a parent passing a fresh arrow each render does not
  // change `load`'s identity and re-run the load effect on every render.
  const onPeriodsLoadedRef = useRef(onPeriodsLoaded);
  useEffect(() => {
    onPeriodsLoadedRef.current = onPeriodsLoaded;
  });

  const load = useCallback(() => {
    if (!companyId) return;
    setIsLoading(true);
    store.makeRequest({
      endpoint: {
        ...API_ENDPOINTS.SUBSCRIPTION_LIST_PERIODS,
        params: { company_id: companyId }
      },
      onSuccess: (data) => {
        const list = data?.periods || [];
        setPeriods(list);
        setActiveId(data?.active_period_id || null);
        setError(null);
        // The form above locks its limit and expiry fields once the company has
        // a period, because the periods set them from then on.
        onPeriodsLoadedRef.current?.(list);
      },
      onError: (msg) => {
        setError(msg || 'Failed to load contract periods');
        onPeriodsLoadedRef.current?.(null);
      },
      onFinally: () => setIsLoading(false)
    });
  }, [companyId, store]);

  useEffect(() => {
    load();
    return () => cancelRequest(API_ENDPOINTS.SUBSCRIPTION_LIST_PERIODS);
  }, [load]);

  const save = (endpoint, body, onDone) => {
    setIsSaving(true);
    setNotice(null);
    store.makeRequest({
      endpoint,
      body,
      onSuccess: (data) => {
        setEditing(null);
        setEnding(null);
        // The company row is mirrored from the active period by the same
        // request, so the form above this panel is stale the moment a period
        // changes. Hand the fresh row up rather than letting an admin save the
        // limits they can still see.
        if (data?.company) onCompanyChanged?.(data.company);
        onDone?.(data);
        load();
      },
      onError: (msg) => setError(msg || 'Failed to save the contract period'),
      onFinally: () => setIsSaving(false)
    });
  };

  const handleSubmit = (values) => {
    if (editing?.id) {
      save(API_ENDPOINTS.SUBSCRIPTION_UPDATE_PERIOD, { id: editing.id, ...values });
      return;
    }
    save(API_ENDPOINTS.SUBSCRIPTION_CREATE_PERIOD, { company_id: companyId, ...values });
  };

  const startEdit = (period) => {
    setEnding(null);
    setError(null);
    setEditing(period);
  };

  const startEndEarly = (period) => {
    setEditing(null);
    setError(null);
    setEnding(period);
    // Prefilled with the current end, so shortening a term by a week is an
    // edit of a date rather than typing one from scratch.
    setEndDate(isoDay(period.ends_on) || todayIso());
  };

  const confirmEndEarly = () => {
    if (!ending) return;
    if (!endDate) {
      setError('Enter the date the period ends on.');
      return;
    }
    if (endDate < isoDay(ending.starts_on)) {
      setError('The end date must not be before the start date.');
      return;
    }
    const period = ending;
    save(API_ENDPOINTS.SUBSCRIPTION_END_PERIOD, { id: period.id, ends_on: endDate }, (data) =>
      setNotice(
        describeEnd(
          period,
          endDate,
          data?.removed_periods?.length || 0,
          data?.auto_renew_stopped === true
        )
      )
    );
  };

  const hasCarryOver = periods.some(
    (p) =>
      Number(p.carried_downloads) > 0 ||
      Number(p.carried_tokens) > 0 ||
      Number(p.carried_credits) > 0
  );
  const lastEnd = periods.length ? periods[periods.length - 1].ends_on : null;
  const totalDays = periods.reduce((sum, p) => sum + spanInDays(p, lastEnd), 0) || 1;

  // Only the first load takes the panel away. Reloading after a save would
  // otherwise blank the table the admin is reading, once per edit.
  if (isLoading && periods.length === 0) {
    return (
      <div className="subscription-form-section">
        <h4>Contract periods</h4>
        <div className="subscription-form-loading">Loading contract periods...</div>
      </div>
    );
  }

  return (
    <div className="subscription-form-section contract-periods">
      <h4>Contract periods</h4>

      {error && (
        <div className="subscription-form-error" role="alert">
          {error}
        </div>
      )}

      {notice && <output className="subscription-form-note">{notice}</output>}

      {periods.length === 0 && (
        <div className="subscription-form-note">
          No periods recorded. Usage is counted from the contract start, as it was before periods
          existed. Adding one starts counting against it.
        </div>
      )}

      {periods.length > 0 && (
        <div className="contract-timeline" aria-hidden="true">
          {periods.map((period) => (
            <div
              key={period.id}
              className={`contract-timeline-segment${period.id === activeId ? ' is-active' : ''}`}
              style={{ flexGrow: spanInDays(period, lastEnd) / totalDays }}
              title={`${formatPeriodDate(period.starts_on)} – ${formatPeriodDate(period.ends_on)}`}
            >
              {/* The bar is decorative and hidden from assistive tech, so it
                  carries the ordinal rather than the label — the label itself
                  stays in the table below, where it can actually be read. */}
              <span className="contract-timeline-label">{period.seq}</span>
            </div>
          ))}
        </div>
      )}

      {periods.length > 0 && (
        <table className="contract-periods-table">
          <thead>
            <tr>
              <th>#</th>
              <th>Period</th>
              <th>Free</th>
              <th>Downloads</th>
              <th>Tokens</th>
              <th>Credits</th>
              <th>Seats</th>
              <th aria-label="Actions" />
            </tr>
          </thead>
          <tbody>
            {periods.map((period) => (
              <tr key={period.id} className={period.id === activeId ? 'is-active' : ''}>
                <td>{period.seq}</td>
                <td>
                  <span className="contract-period-range">
                    {formatPeriodDate(period.starts_on)} – {formatPeriodDate(period.ends_on)}
                  </span>
                  {period.id === activeId && <span className="contract-period-badge">Active</span>}
                  {period.label && <div className="contract-period-label">{period.label}</div>}
                </td>
                <td>{Number(period.free_months) || 0}</td>
                <td>{formatLimit(period.profiler_download_limit, period.carried_downloads)}</td>
                <td>{formatLimit(period.agent_token_limit, period.carried_tokens)}</td>
                <td>{formatLimit(period.enrichment_credit_limit, period.carried_credits)}</td>
                {/* Seats are a concurrent-use cap and weekly tokens reset
                    weekly, so neither carries anything. */}
                <td>{formatLimit(period.max_concurrent_users)}</td>
                <td className="contract-periods-actions">
                  <button type="button" onClick={() => startEdit(period)}>
                    Edit
                  </button>
                  <button type="button" onClick={() => startEndEarly(period)}>
                    End early
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {hasCarryOver && (
        <div className="subscription-form-note">
          A &quot;+ n&quot; is allowance carried over from the period before. The limit the platform
          enforces is the total, which is the figure shown in the fields above.
        </div>
      )}

      {editing && (
        <PeriodForm
          // Keyed so picking a different row rebuilds the form. Without it
          // React keeps the mounted instance and its state, and the admin edits
          // period 2 in a form still holding period 1's dates.
          key={editing.id || 'new'}
          period={editing.id ? editing : null}
          isSaving={isSaving}
          onCancel={() => setEditing(null)}
          onSubmit={handleSubmit}
        />
      )}

      {!editing && ending && (
        <div className="contract-period-form">
          <div className="contract-period-form-row">
            <div className="subscription-form-group">
              <label htmlFor="period-end-early-date">
                End period {ending.seq} on{' '}
                <span className="required-indicator" aria-hidden="true">
                  *
                </span>
              </label>
              <input
                id="period-end-early-date"
                type="date"
                value={endDate}
                min={isoDay(ending.starts_on)}
                onChange={(e) => setEndDate(e.target.value)}
              />
              <span className="subscription-form-hint">
                A period entered after this one keeps its own start date, and usage counts in this
                period until it starts. An automatic renewal that has not started yet is removed
                with it; one that has started is ended the same way. If the new end is earlier and
                no period follows, automatic renewal is switched off.
              </span>
            </div>
          </div>
          <div className="subscription-form-actions">
            <button
              type="button"
              className="subscription-btn-cancel"
              onClick={() => setEnding(null)}
            >
              Cancel
            </button>
            <button
              type="button"
              className="subscription-btn-save"
              onClick={confirmEndEarly}
              disabled={isSaving}
            >
              {isSaving ? 'Saving...' : 'End period'}
            </button>
          </div>
        </div>
      )}

      {!editing && !ending && (
        <button
          type="button"
          className="subscription-btn-add-domain contract-periods-add"
          onClick={() => setEditing({})}
        >
          Add period
        </button>
      )}
    </div>
  );
};

ContractPeriods.propTypes = {
  companyId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onCompanyChanged: PropTypes.func,
  /** Called with the company's periods, or with null when they could not be loaded. */
  onPeriodsLoaded: PropTypes.func
};

export default ContractPeriods;
